import { useCallback, useMemo, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { orgApi } from '../services/api';
import { useAuth } from '../store/auth-context';
import type {
  DotplotResponse,
  SummaryResponse,
  CatalogResponse,
} from '../types/analytics';

function useOrgId(): string {
  const { user } = useAuth();
  return user?.orgId ?? '';
}

function useAnalyticsApi() {
  const orgId = useOrgId();
  return useMemo(() => orgApi(orgId).analytics, [orgId]);
}

/**
 * Poll every 3 s while the report is still computing (202 → `pending`), then
 * stop. `meta.stale` on a ready body doesn't force polling — the page shows a
 * subtle "updating…" hint instead (the served data is already usable).
 */
function pendingInterval(status: string | undefined): number | false {
  return status === 'pending' ? 3000 : false;
}

/**
 * The `refresh` flag is a one-shot: callers hit `forceRefresh()`, which arms a
 * ref and refetches with `?refresh=true`; the ref clears itself on that fetch
 * so subsequent polls don't keep forcing recomputes. `keepPreviousData` serves
 * the stale body while the recompute runs, matching the backend contract.
 */
export function useDotplot(params: { days: number; limit?: number }) {
  const orgId = useOrgId();
  const api = useAnalyticsApi();
  const { days, limit } = params;
  const forceRef = useRef(false);

  const query = useQuery<DotplotResponse>({
    queryKey: ['org', orgId, 'analytics', 'dotplot', days, limit ?? null],
    queryFn: () => {
      const refresh = forceRef.current;
      forceRef.current = false;
      return api.getDotplot({ days, limit, refresh });
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => pendingInterval(q.state.data?.status),
  });

  const forceRefresh = useCallback(() => {
    forceRef.current = true;
    void query.refetch();
  }, [query]);

  return { query, forceRefresh };
}

export function useSummary(params: { days: number }) {
  const orgId = useOrgId();
  const api = useAnalyticsApi();
  const { days } = params;
  const forceRef = useRef(false);

  const query = useQuery<SummaryResponse>({
    queryKey: ['org', orgId, 'analytics', 'summary', days],
    queryFn: () => {
      const refresh = forceRef.current;
      forceRef.current = false;
      return api.getSummary({ days, refresh });
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => pendingInterval(q.state.data?.status),
  });

  const forceRefresh = useCallback(() => {
    forceRef.current = true;
    void query.refetch();
  }, [query]);

  return { query, forceRefresh };
}

/** Static category catalog + palette. Rarely changes, so cache it hard. */
export function useAnalyticsCatalog() {
  const orgId = useOrgId();
  const api = useAnalyticsApi();
  return useQuery<CatalogResponse>({
    queryKey: ['org', orgId, 'analytics', 'catalog'],
    queryFn: () => api.getCatalog(),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 30,
  });
}
